import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  carregarArvoreAdmin,
  carregarBlocosAdmin,
  excluir,
  reordenar,
  salvarBloco,
  salvarPagina,
  salvarSecao,
  subirImagem,
  urlDaImagem,
  type BlocoEditavel,
  type PaginaAdmin,
  type SecaoAdmin,
  type TipoDeBloco,
  type Tom,
} from "../lib/conteudo";
import { envolver, paraTextoSimples } from "../lib/textoRico";
import TextoRico from "./TextoRico";
import { CAMPO, Recado } from "./ui";
import { Vazio } from "./painel";
import {
  IconeAbrirPasta,
  IconeEditar,
  IconeFechar,
  IconeLixeira,
  IconeOlho,
  IconeOlhoRiscado,
} from "./icones";

/**
 * Admin → Conteúdo: as páginas que a equipe edita.
 *
 * ## Quem usa isto
 *
 * Victor, em 20/08: *"nossos admins de conteúdo são meio leigos, então tem que
 * ficar bem intuitivo"*. É a restrição que manda em cada decisão abaixo.
 *
 * A primeira versão desta aba era compacta e opaca: um "Editar" em texto pequeno,
 * a barra de marcação com as letras `N` `S` `D`, e nenhum jeito de renomear uma
 * área — a RPC aceitava `nome` desde a 000073 e a tela nunca ligou o campo. A
 * segunda foi para o outro extremo: quatro PÍLULAS de verbo por extenso em cada
 * linha, e Victor pediu ícones no mesmo dia ("procure usar ícone nos que der").
 * O equilíbrio que ficou: **ação de linha é ÍCONE com a frase inteira no `title`
 * e no `aria-label`**; texto continua onde carrega o que ícone não carrega — as
 * instruções de cada nível, a ajuda de cada tipo de bloco, as contagens e as
 * confirmações de excluir. A barra de marcação segue por extenso (Negrito,
 * Sublinhado, Destacar): ali as letras soltas foram o problema original.
 *
 * ## Três níveis, uma tela
 *
 * Área → página → blocos, com trilha de volta e um lembrete do que é cada nível.
 * Não são três abas porque o trabalho real é "abrir a página e mexer nos blocos
 * dela": três abas obrigariam a reescolher a área a cada ida e volta.
 *
 * ## O que esta tela NÃO decide
 *
 * Quem pode editar é `pode_editar('conteudo')` (000297), DENTRO de cada RPC. A tela não esconde botão
 * para se proteger — ela some da lista de abas para quem não é operador, e o banco
 * recusa o resto. Se esta fosse a trava, bastaria desligar o JavaScript.
 */

/** As ferramentas que usam o motor de conteúdo. */
const FERRAMENTAS = [
  { card: "ir-pro", nome: "IR PRO", migrado: true },
  // Migrado em 20/08 (000076): 9 seções, 43 páginas, 1.432 blocos.
  { card: "mei-pro", nome: "MEI PRO", migrado: true },
];

const TIPOS: { tipo: TipoDeBloco; rotulo: string; ajuda: string }[] = [
  { tipo: "titulo", rotulo: "Título", ajuda: "Um subtítulo, para dividir a página em partes." },
  { tipo: "paragrafo", rotulo: "Parágrafo", ajuda: "Texto corrido — o bloco mais comum." },
  { tipo: "lista", rotulo: "Lista", ajuda: "Itens em sequência, um por linha." },
  { tipo: "destaque", rotulo: "Destaque", ajuda: "Uma caixa colorida, para um aviso importante." },
  { tipo: "imagem", rotulo: "Imagem", ajuda: "Um print ou ilustração, com legenda opcional." },
  { tipo: "video", rotulo: "Vídeo", ajuda: "Um vídeo do YouTube." },
];

const TONS: { tom: Tom; rotulo: string; ajuda: string }[] = [
  { tom: "info", rotulo: "Informação", ajuda: "azul — uma observação" },
  { tom: "atencao", rotulo: "Atenção", ajuda: "laranja — cuidado, prazo, erro comum" },
  { tom: "positivo", rotulo: "Positivo", ajuda: "verde — uma dica, um caminho fácil" },
  { tom: "neutro", rotulo: "Neutro", ajuda: "cinza — um lembrete" },
];

const rotuloDoTipo = (t: string) => TIPOS.find((x) => x.tipo === t)?.rotulo ?? t;

export default function AdminConteudo() {
  const [card, setCard] = useState("ir-pro");
  const [arvore, setArvore] = useState<SecaoAdmin[] | null>(null);
  const [secaoAberta, setSecaoAberta] = useState<string | null>(null);
  const [paginaAberta, setPaginaAberta] = useState<string | null>(null);
  const [recado, setRecado] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);

  const recarregar = useCallback(async () => {
    setArvore(await carregarArvoreAdmin(card));
  }, [card]);

  useEffect(() => {
    setArvore(null);
    setSecaoAberta(null);
    setPaginaAberta(null);
    void recarregar();
  }, [recarregar]);

  const ferramenta = FERRAMENTAS.find((f) => f.card === card)!;
  const secao = arvore?.find((s) => s.id === secaoAberta) ?? null;
  const pagina = secao?.paginas.find((p) => p.id === paginaAberta) ?? null;

  const agir: Agir = async (promessa, sucesso) => {
    const r = await promessa;
    if (!r.ok) return setRecado({ tipo: "erro", texto: r.mensagem ?? "Não deu." });
    await recarregar();
    setRecado({ tipo: "ok", texto: sucesso });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Ferramenta</span>
          <select
            value={card}
            onChange={(e) => setCard(e.target.value)}
            className={`${CAMPO} w-64`}
          >
            {FERRAMENTAS.map((f) => (
              <option key={f.card} value={f.card}>
                {f.nome}
                {f.migrado ? "" : " — ainda no Bubble"}
              </option>
            ))}
          </select>
        </label>

        {ferramenta.migrado && (
          // Ver o resultado é parte de editar. Sem este link o admin publica e
          // depois procura a ferramenta pelo menu do aluno para conferir.
          <a
            href={
              pagina && secao
                ? `/ferramenta/${card}/${secao.slug}/${pagina.slug}`
                : `/ferramenta/${card}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="btn-contorno shrink-0"
          >
            Ver como o aluno vê ↗
          </a>
        )}
      </div>

      {ferramenta.migrado && (
        <Trilha
          secao={secao}
          pagina={pagina}
          aoIrParaAreas={() => {
            setSecaoAberta(null);
            setPaginaAberta(null);
          }}
          aoIrParaPaginas={() => setPaginaAberta(null)}
        />
      )}

      {!ferramenta.migrado ? (
        <Vazio>
          O conteúdo do {ferramenta.nome} ainda está no Bubble. O motor de conteúdo é o mesmo — o
          que falta é a carga, e a decisão sobre o livro-caixa, que tem 124 mil lançamentos e
          clientes com login próprio.
        </Vazio>
      ) : arvore === null ? (
        <Vazio>Carregando…</Vazio>
      ) : pagina && secao ? (
        <EditorDeBlocos
          card={card}
          secao={secao}
          pagina={pagina}
          aoRecarregar={recarregar}
          aoAvisar={setRecado}
        />
      ) : secao ? (
        <ListaDePaginas secao={secao} aoAbrir={setPaginaAberta} aoAgir={agir} />
      ) : (
        <ListaDeSecoes card={card} arvore={arvore} aoAbrir={setSecaoAberta} aoAgir={agir} />
      )}

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </div>
  );
}

type Agir = (
  p: Promise<{ ok: boolean; mensagem?: string }>,
  sucesso: string,
) => Promise<void>;

// ---------------------------------------------------------------------------
// A trilha, com o nome de cada nível
// ---------------------------------------------------------------------------
/**
 * "Áreas › Renda Variável › Como declarar ações".
 *
 * O nome do nível vai junto ("Áreas", "Páginas desta área") porque "área",
 * "página" e "bloco" são o vocabulário que o resto da tela usa nos botões. Trilha
 * que só mostra os nomes obriga quem é leigo a inferir a hierarquia.
 */
function Trilha({
  secao,
  pagina,
  aoIrParaAreas,
  aoIrParaPaginas,
}: {
  secao: SecaoAdmin | null;
  pagina: PaginaAdmin | null;
  aoIrParaAreas: () => void;
  aoIrParaPaginas: () => void;
}) {
  return (
    <nav aria-label="Onde estou" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <button
        type="button"
        onClick={aoIrParaAreas}
        disabled={!secao}
        className={
          secao
            ? "font-semibold text-link underline decoration-link/30 underline-offset-4"
            : "font-semibold text-tinta"
        }
      >
        Todas as áreas
      </button>
      {secao && (
        <>
          <span className="text-cinza" aria-hidden="true">
            ›
          </span>
          <button
            type="button"
            onClick={aoIrParaPaginas}
            disabled={!pagina}
            className={
              pagina
                ? "font-semibold text-link underline decoration-link/30 underline-offset-4"
                : "font-semibold text-tinta"
            }
          >
            {secao.nome}
          </button>
        </>
      )}
      {pagina && (
        <>
          <span className="text-cinza" aria-hidden="true">
            ›
          </span>
          <span className="font-semibold text-tinta">{pagina.nome}</span>
        </>
      )}
    </nav>
  );
}

/** Um lembrete curto do que fazer neste nível. Some quando a lista está cheia? Não: leigo relê. */
function Instrucao({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-xl bg-superficie-2 p-3.5 text-sm leading-relaxed text-cinza">
      {children}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Nível 1 — as áreas
// ---------------------------------------------------------------------------
function ListaDeSecoes({
  card,
  arvore,
  aoAbrir,
  aoAgir,
}: {
  card: string;
  arvore: SecaoAdmin[];
  aoAbrir: (id: string) => void;
  aoAgir: Agir;
}) {
  const [renomeando, setRenomeando] = useState<string | null>(null);

  return (
    <div className="space-y-3">
      <Instrucao>
        <strong className="text-tinta">Área</strong> é um item do menu da ferramenta — "Renda
        Fixa", "Criptoativos". Dentro de cada área ficam as páginas. Clique numa área para ver as
        páginas dela.
      </Instrucao>

      {arvore.map((s, i) => (
        <div key={s.id} className="surface p-4">
          {renomeando === s.id ? (
            <FormularioDeNome
              titulo="Renomear a área"
              nome={s.nome}
              slug={s.slug}
              enderecoDe={(slug) => `/ferramenta/${card}/${slug}`}
              aoCancelar={() => setRenomeando(null)}
              aoSalvar={async (nome, slug) => {
                await aoAgir(salvarSecao({ id: s.id, nome, slug }), "Área renomeada.");
                setRenomeando(null);
              }}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Setas
                i={i}
                total={arvore.length}
                oQue="área"
                aoMover={(de, para) =>
                  aoAgir(
                    reordenar("secoes", card, trocar(arvore.map((x) => x.id), de, para)),
                    "Ordem das áreas salva.",
                  )
                }
              />
              <button
                type="button"
                onClick={() => aoAbrir(s.id)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="font-semibold text-titulo">{s.nome}</span>
                <span className="mt-0.5 block text-sm text-cinza">
                  {s.paginas.length} {s.paginas.length === 1 ? "página" : "páginas"} · endereço{" "}
                  <code className="text-xs">/{s.slug}</code>
                </span>
              </button>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Botao onClick={() => aoAbrir(s.id)} title="Abrir as páginas desta área" icone={<IconeAbrirPasta />} />
                <Botao onClick={() => setRenomeando(s.id)} title="Renomear a área" icone={<IconeEditar />} />
                <Visibilidade
                  publicada={s.publicada}
                  aoTrocar={() =>
                    aoAgir(
                      salvarSecao({ id: s.id, publicada: !s.publicada }),
                      s.publicada
                        ? "Área escondida dos alunos."
                        : "Área publicada — os alunos já veem.",
                    )
                  }
                />
                <Excluir
                  oQue="a área"
                  nome={s.nome}
                  extra={`${s.paginas.length} página(s) e todos os blocos delas`}
                  aoConfirmar={() => aoAgir(excluir("secao", s.id), "Área excluída.")}
                />
              </div>
            </div>
          )}
        </div>
      ))}

      <NovoItem
        oQue="área"
        exemplo="Renda Fixa"
        aoCriar={(nome, slug) =>
          aoAgir(
            salvarSecao({ card, nome, slug, publicada: false }),
            "Área criada. Ela começa escondida — publique quando estiver pronta.",
          )
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nível 2 — as páginas
// ---------------------------------------------------------------------------
function ListaDePaginas({
  secao,
  aoAbrir,
  aoAgir,
}: {
  secao: SecaoAdmin;
  aoAbrir: (id: string) => void;
  aoAgir: Agir;
}) {
  const [renomeando, setRenomeando] = useState<string | null>(null);

  return (
    <div className="space-y-3">
      <Instrucao>
        <strong className="text-tinta">Página</strong> é um assunto dentro da área — o aluno abre
        uma por vez. Clique numa página para escrever o conteúdo dela.
      </Instrucao>

      {secao.paginas.length === 0 && (
        <Vazio>Esta área ainda não tem página nenhuma. Crie a primeira abaixo.</Vazio>
      )}

      {secao.paginas.map((p, i) => (
        <div key={p.id} className="surface p-4">
          {renomeando === p.id ? (
            <FormularioDeNome
              titulo="Renomear a página"
              nome={p.nome}
              slug={p.slug}
              enderecoDe={(slug) => `/${secao.slug}/${slug}`}
              aoCancelar={() => setRenomeando(null)}
              aoSalvar={async (nome, slug) => {
                await aoAgir(salvarPagina({ id: p.id, nome, slug }), "Página renomeada.");
                setRenomeando(null);
              }}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Setas
                i={i}
                total={secao.paginas.length}
                oQue="página"
                aoMover={(de, para) =>
                  aoAgir(
                    reordenar(
                      "paginas",
                      secao.id,
                      trocar(secao.paginas.map((x) => x.id), de, para),
                    ),
                    "Ordem das páginas salva.",
                  )
                }
              />
              <button
                type="button"
                onClick={() => aoAbrir(p.id)}
                className="min-w-0 flex-1 text-left"
              >
                <span className="font-semibold text-titulo">{p.nome}</span>
                <span className="mt-0.5 block text-sm text-cinza">
                  {p.blocos === 0 ? (
                    <span className="font-semibold text-alerta-tinta">
                      Nenhum conteúdo escrito ainda
                    </span>
                  ) : (
                    <>
                      {p.blocos} {p.blocos === 1 ? "bloco" : "blocos"} de conteúdo
                    </>
                  )}{" "}
                  · endereço <code className="text-xs">/{p.slug}</code>
                </span>
              </button>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Botao onClick={() => aoAbrir(p.id)} title="Escrever o conteúdo desta página" icone={<IconeAbrirPasta />} />
                <Botao onClick={() => setRenomeando(p.id)} title="Renomear a página" icone={<IconeEditar />} />
                <Visibilidade
                  publicada={p.publicada}
                  aoTrocar={() =>
                    aoAgir(
                      salvarPagina({ id: p.id, publicada: !p.publicada }),
                      p.publicada
                        ? "Página escondida dos alunos."
                        : "Página publicada — os alunos já veem.",
                    )
                  }
                />
                <Excluir
                  oQue="a página"
                  nome={p.nome}
                  extra={`${p.blocos} bloco(s) de conteúdo`}
                  aoConfirmar={() => aoAgir(excluir("pagina", p.id), "Página excluída.")}
                />
              </div>
            </div>
          )}
        </div>
      ))}

      <NovoItem
        oQue="página"
        exemplo="Como declarar ações"
        aoCriar={(nome, slug) =>
          aoAgir(
            salvarPagina({ secao: secao.id, nome, slug, publicada: false }),
            "Página criada. Ela começa escondida — publique quando estiver pronta.",
          )
        }
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nível 3 — os blocos
// ---------------------------------------------------------------------------
function EditorDeBlocos({
  card,
  secao,
  pagina,
  aoRecarregar,
  aoAvisar,
}: {
  card: string;
  secao: SecaoAdmin;
  pagina: PaginaAdmin;
  aoRecarregar: () => Promise<void>;
  aoAvisar: (r: { tipo: "ok" | "erro"; texto: string }) => void;
}) {
  const [blocos, setBlocos] = useState<BlocoEditavel[] | null>(null);
  const [editando, setEditando] = useState<string | null>(null);

  const recarregar = useCallback(async () => {
    setBlocos(await carregarBlocosAdmin(pagina.id));
    await aoRecarregar();
  }, [pagina.id, aoRecarregar]);

  useEffect(() => {
    setBlocos(null);
    setEditando(null);
    void recarregar();
  }, [recarregar]);

  async function agir(promessa: Promise<{ ok: boolean; mensagem?: string }>, sucesso: string) {
    const r = await promessa;
    if (!r.ok) return aoAvisar({ tipo: "erro", texto: r.mensagem ?? "Não deu." });
    await recarregar();
    aoAvisar({ tipo: "ok", texto: sucesso });
  }

  if (blocos === null) return <Vazio>Carregando o conteúdo…</Vazio>;

  return (
    <div className="space-y-3">
      <Instrucao>
        A página é feita de <strong className="text-tinta">blocos</strong>, um embaixo do outro, na
        ordem em que aparecem para o aluno. Clique em <strong className="text-tinta">Editar</strong>{" "}
        para mudar um bloco, use <strong className="text-tinta">▲▼</strong> para trocar a ordem, e
        escolha um tipo lá embaixo para acrescentar.
        {!pagina.publicada && (
          <>
            {" "}
            <span className="font-semibold text-alerta-tinta">
              Esta página está escondida: ninguém vê o que você escrever aqui até publicá-la.
            </span>
          </>
        )}
      </Instrucao>

      {blocos.length === 0 && (
        <Vazio>
          Página em branco. Comece por um <strong>Título</strong> ou um{" "}
          <strong>Parágrafo</strong>, nos botões lá embaixo.
        </Vazio>
      )}

      {blocos.map((b, i) => (
        <div key={b.id} className="surface p-4">
          <div className="flex flex-wrap items-start gap-3">
            <Setas
              i={i}
              total={blocos.length}
              oQue="bloco"
              aoMover={(de, para) =>
                agir(
                  reordenar("blocos", pagina.id, trocar(blocos.map((x) => x.id), de, para)),
                  "Ordem dos blocos salva.",
                )
              }
            />
            <div className="min-w-0 flex-1">
              <span className="rounded-full bg-chip px-2.5 py-0.5 text-xs font-bold text-chip-tinta">
                {rotuloDoTipo(b.tipo)}
              </span>
              {/* `line-clamp-2`: há página com 110 blocos ("Apuração Passo a
                  Passo"), e resumo de três linhas por bloco transforma a lista em
                  rolagem. Duas linhas bastam para reconhecer o bloco. */}
              <div className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-cinza">
                {resumoDoBloco(b)}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Botao onClick={() => setEditando(editando === b.id ? null : b.id)} title={editando === b.id ? "Fechar a edição" : "Editar o bloco"} icone={editando === b.id ? <IconeFechar /> : <IconeEditar />} />
              <Excluir
                oQue="o bloco"
                nome={rotuloDoTipo(b.tipo)}
                aoConfirmar={() => agir(excluir("bloco", b.id), "Bloco excluído.")}
              />
            </div>
          </div>

          {editando === b.id && (
            <FormularioDeBloco
              card={card}
              bloco={b}
              aoCancelar={() => setEditando(null)}
              aoSalvar={async (campos) => {
                await agir(salvarBloco({ id: b.id, ...campos }), "Bloco salvo.");
                setEditando(null);
              }}
            />
          )}
        </div>
      ))}

      <div className="surface p-4">
        <p className="text-sm font-semibold text-titulo">Acrescentar um bloco no fim da página</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {TIPOS.map((t) => (
            <button
              key={t.tipo}
              type="button"
              onClick={() =>
                agir(
                  salvarBloco({
                    pagina: pagina.id,
                    tipo: t.tipo,
                    // Cada tipo nasce com o mínimo que a constraint do banco exige.
                    // Bloco criado vazio seria recusado com uma mensagem do Postgres
                    // que não ajuda quem está escrevendo.
                    texto:
                      t.tipo === "imagem" || t.tipo === "video"
                        ? null
                        : t.tipo === "titulo"
                          ? "Novo título"
                          : "Escreva o texto aqui.",
                    itens: t.tipo === "lista" ? ["Primeiro item"] : null,
                    ordenada: t.tipo === "lista" ? false : null,
                    imagem_path: t.tipo === "imagem" ? `${card}/a-escolher` : null,
                    imagem_alt: t.tipo === "imagem" ? "Ilustração" : null,
                    video_id: t.tipo === "video" ? "a-escolher" : null,
                    tom: t.tipo === "destaque" ? "info" : null,
                  }),
                  `${t.rotulo} acrescentado no fim da página. Clique em Editar para preencher.`,
                )
              }
              className="rounded-xl border-[1.5px] border-borda p-3 text-left transition hover:border-laranja hover:bg-chip/40"
            >
              <span className="block text-sm font-semibold text-titulo">+ {t.rotulo}</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-cinza">{t.ajuda}</span>
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-cinza">
        Terminou?{" "}
        <a
          href={`/ferramenta/${card}/${secao.slug}/${pagina.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-link underline decoration-link/30 underline-offset-4"
        >
          Abra a página como o aluno vê ↗
        </a>{" "}
        para conferir.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Renomear — nome livre, endereço protegido
// ---------------------------------------------------------------------------
/**
 * O NOME muda à vontade; o ENDEREÇO só com o aviso na frente.
 *
 * Os dois vêm juntos na criação (o endereço nasce do nome), e por isso é fácil
 * achar que renomear muda os dois. Não muda, e não pode: o endereço está em links
 * que a equipe já mandou em aula e que aluno já guardou. Trocá-lo transforma esses
 * links em "assunto não disponível" sem avisar ninguém.
 *
 * Daí o endereço ficar atrás de um "Mudar o endereço" fechado, com a consequência
 * escrita. Quem é leigo renomeia e vai embora; quem precisa mudar o endereço lê o
 * aviso antes.
 */
function FormularioDeNome({
  titulo,
  nome: nomeInicial,
  slug: slugInicial,
  enderecoDe,
  aoSalvar,
  aoCancelar,
}: {
  titulo: string;
  nome: string;
  slug: string;
  enderecoDe: (slug: string) => string;
  aoSalvar: (nome: string, slug: string | undefined) => Promise<void>;
  aoCancelar: () => void;
}) {
  const [nome, setNome] = useState(nomeInicial);
  const [slug, setSlug] = useState(slugInicial);
  const [mexerNoEndereco, setMexerNoEndereco] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (nome.trim() === "") return setErro("O nome não pode ficar vazio.");
    if (mexerNoEndereco && slug.trim() === "") return setErro("O endereço não pode ficar vazio.");
    setErro(null);
    setSalvando(true);
    await aoSalvar(nome.trim(), mexerNoEndereco ? slug : undefined);
    setSalvando(false);
  }

  return (
    <form onSubmit={enviar}>
      <p className="text-sm font-semibold text-titulo">{titulo}</p>
      <label className="mt-3 block">
        <span className="mb-1 block text-sm font-semibold text-titulo">
          Nome que o aluno lê
        </span>
        <input
          autoFocus
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className={CAMPO}
        />
      </label>

      {!mexerNoEndereco ? (
        <p className="mt-2 text-sm text-cinza">
          Endereço: <code className="text-xs">{enderecoDe(slugInicial)}</code>{" "}
          <button
            type="button"
            onClick={() => setMexerNoEndereco(true)}
            className="font-semibold text-link underline decoration-link/30 underline-offset-4"
          >
            Mudar o endereço
          </button>
        </p>
      ) : (
        <label className="mt-3 block">
          <span className="mb-1 block text-sm font-semibold text-titulo">Endereço</span>
          <input
            value={slug}
            onChange={(e) => setSlug(slugificar(e.target.value))}
            className={CAMPO}
          />
          <span className="mt-1 block rounded-xl bg-alerta-fundo p-3 text-sm leading-relaxed text-alerta-tinta">
            Trocar o endereço <strong>quebra os links já compartilhados</strong> desta página —
            quem tiver o link antigo vai ver "assunto não disponível". Mude só se ninguém tiver
            recebido o endereço ainda.
          </span>
        </label>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="submit" disabled={salvando} className="pill-cta disabled:opacity-60">
          {salvando ? "Salvando…" : "Salvar"}
        </button>
        <button type="button" onClick={aoCancelar} className="btn-contorno">
          Cancelar
        </button>
      </div>
      {erro && <Recado tipo="erro" texto={erro} />}
    </form>
  );
}

// ---------------------------------------------------------------------------
// O formulário de um bloco
// ---------------------------------------------------------------------------
function FormularioDeBloco({
  card,
  bloco,
  aoSalvar,
  aoCancelar,
}: {
  card: string;
  bloco: BlocoEditavel;
  aoSalvar: (campos: {
    tipo: TipoDeBloco;
    texto: string | null;
    itens: string[] | null;
    ordenada: boolean | null;
    imagem_path: string | null;
    imagem_alt: string | null;
    video_id: string | null;
    tom: Tom | null;
  }) => Promise<void>;
  aoCancelar: () => void;
}) {
  const [tipo, setTipo] = useState<TipoDeBloco>(bloco.tipo);
  const [texto, setTexto] = useState(bloco.texto ?? "");
  const [itens, setItens] = useState((bloco.itens ?? []).join("\n"));
  const [ordenada, setOrdenada] = useState(bloco.ordenada ?? false);
  const [imagemPath, setImagemPath] = useState(bloco.imagem_path ?? "");
  const [imagemAlt, setImagemAlt] = useState(bloco.imagem_alt ?? "");
  const [videoId, setVideoId] = useState(bloco.video_id ?? "");
  const [tom, setTom] = useState<Tom>(bloco.tom ?? "info");
  const [subindo, setSubindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const caixa = useRef<HTMLTextAreaElement>(null);

  const usaTexto = tipo === "titulo" || tipo === "paragrafo" || tipo === "destaque";
  const usaLegenda = tipo === "imagem" || tipo === "video";
  const imagemEscolhida = imagemPath !== "" && !imagemPath.endsWith("a-escolher");
  const videoEscolhido = videoId !== "" && videoId !== "a-escolher";

  /**
   * Envolve a seleção na marca, como faria uma barra de rich text.
   *
   * O espaço das PONTAS fica FORA da marca. Duplo clique numa palavra costuma levar
   * o espaço seguinte, e `** 31 de maio**` renderiza um negrito que começa antes da
   * palavra — a mesma sujeira que o conversor do Bubble limpou em 48 lugares.
   */
  function marcar(marca: "negrito" | "sublinhado" | "marcado") {
    const el = caixa.current;
    if (!el) return;
    const { selectionStart: a, selectionEnd: b, value } = el;
    if (a === b) return setErro("Selecione com o mouse o trecho que você quer marcar, e clique de novo.");

    const bruto = value.slice(a, b);
    const antes = bruto.match(/^\s*/)?.[0] ?? "";
    const depois = bruto.match(/\s*$/)?.[0] ?? "";
    const nucleo = bruto.slice(antes.length, bruto.length - depois.length);
    if (nucleo === "") return setErro("Selecione um trecho com texto.");
    setErro(null);

    const novo = value.slice(0, a) + antes + envolver(nucleo, marca) + depois + value.slice(b);
    setTexto(novo);
    const inicio = a + antes.length + 2;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(inicio, inicio + nucleo.length);
    });
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    const listaLimpa = itens
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l !== "");
    if (tipo === "lista" && listaLimpa.length === 0) {
      return setErro("Uma lista precisa de pelo menos um item. Escreva um por linha.");
    }
    if (tipo === "imagem" && !imagemEscolhida) {
      return setErro("Escolha o arquivo da imagem no botão acima.");
    }
    if (tipo === "video" && !videoEscolhido) {
      return setErro("Cole o endereço do vídeo do YouTube.");
    }
    if (usaTexto && texto.trim() === "") {
      return setErro("Este bloco precisa de texto.");
    }

    // Cada tipo manda só os campos DELE, e os outros vão NULOS — não omitidos. É
    // o que permite trocar o tipo de um bloco existente sem o check
    // `conteudo_blocos_coerente` recusar por causa de um campo esquecido.
    setSalvando(true);
    await aoSalvar({
      tipo,
      texto: usaTexto || usaLegenda ? (texto.trim() === "" ? null : texto) : null,
      itens: tipo === "lista" ? listaLimpa : null,
      ordenada: tipo === "lista" ? ordenada : null,
      imagem_path: tipo === "imagem" ? imagemPath.trim() : null,
      imagem_alt: tipo === "imagem" ? imagemAlt.trim() || "Ilustração" : null,
      video_id: tipo === "video" ? videoId.trim() : null,
      tom: tipo === "destaque" ? tom : null,
    });
    setSalvando(false);
  }

  return (
    <form onSubmit={enviar} className="mt-4 border-t border-borda pt-4">
      <label className="block max-w-xs">
        <span className="mb-1 block text-sm font-semibold text-titulo">Tipo deste bloco</span>
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value as TipoDeBloco)}
          className={CAMPO}
        >
          {TIPOS.map((t) => (
            <option key={t.tipo} value={t.tipo}>
              {t.rotulo}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-xs text-cinza">
          {TIPOS.find((t) => t.tipo === tipo)?.ajuda}
        </span>
      </label>

      {tipo === "destaque" && (
        <fieldset className="mt-4">
          <legend className="text-sm font-semibold text-titulo">Cor da caixa</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {TONS.map((t) => (
              <label key={t.tom} className="flex min-h-11 cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name={`tom-${bloco.id}`}
                  checked={tom === t.tom}
                  onChange={() => setTom(t.tom)}
                  className="size-4 accent-laranja"
                />
                <span className="text-sm text-tinta">
                  {t.rotulo} <span className="text-xs text-cinza">({t.ajuda})</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      )}

      {(usaTexto || usaLegenda) && (
        <div className="mt-4">
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-titulo">
              {usaLegenda ? "Legenda (pode deixar em branco)" : "Texto"}
            </span>
            {/*
              Botões com PALAVRA, e não `N` `S` `D`.
              A primeira versão usava as letras, e Victor apontou que a equipe de
              conteúdo é leiga: letra isolada exige que a pessoa já saiba o que a
              barra faz. O custo é largura, e largura aqui não é escassa.
            */}
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <span className="text-xs text-cinza">Selecione um trecho e clique:</span>
              <BotaoDeMarca aoClicar={() => marcar("negrito")}>
                <strong>Negrito</strong>
              </BotaoDeMarca>
              <BotaoDeMarca aoClicar={() => marcar("sublinhado")}>
                <u>Sublinhado</u>
              </BotaoDeMarca>
              <BotaoDeMarca aoClicar={() => marcar("marcado")}>
                <mark className="bg-alerta-fundo px-1 text-alerta-tinta">Destacar</mark>
              </BotaoDeMarca>
            </div>
            <textarea
              ref={caixa}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              rows={usaLegenda ? 2 : 7}
              className={`${CAMPO} h-auto py-3 leading-relaxed`}
            />
          </label>
          <p className="mt-1 text-xs leading-relaxed text-cinza">
            Para criar um link, escreva assim:{" "}
            <code>[texto que aparece](https://endereco.com.br)</code>
          </p>
        </div>
      )}

      {tipo === "lista" && (
        <>
          <label className="mt-4 block">
            <span className="mb-1 block text-sm font-semibold text-titulo">
              Itens da lista — um por linha
            </span>
            <textarea
              value={itens}
              onChange={(e) => setItens(e.target.value)}
              rows={6}
              className={`${CAMPO} h-auto py-3 leading-relaxed`}
            />
          </label>
          <label className="mt-2 flex min-h-11 cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={ordenada}
              onChange={(e) => setOrdenada(e.target.checked)}
              className="size-4 accent-laranja"
            />
            <span className="text-sm text-tinta">
              Numerar os itens (1, 2, 3) em vez de usar bolinhas
            </span>
          </label>
        </>
      )}

      {tipo === "imagem" && (
        <div className="mt-4 space-y-3">
          <div>
            <p className="mb-1 text-sm font-semibold text-titulo">Arquivo da imagem</p>
            <label className="btn-contorno inline-flex cursor-pointer">
              {subindo ? "Enviando…" : imagemEscolhida ? "Trocar a imagem" : "Escolher a imagem"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                disabled={subindo}
                onChange={async (e) => {
                  const arquivo = e.target.files?.[0];
                  if (!arquivo) return;
                  setSubindo(true);
                  setErro(null);
                  const r = await subirImagem(card, arquivo);
                  setSubindo(false);
                  if (!r.ok) return setErro(r.mensagem);
                  setImagemPath(r.res);
                }}
                className="sr-only"
              />
            </label>
            <span className="mt-1 block text-xs text-cinza">PNG, JPG, WEBP ou GIF, até 5 MB.</span>
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-titulo">
              O que a imagem mostra
            </span>
            <input
              value={imagemAlt}
              onChange={(e) => setImagemAlt(e.target.value)}
              placeholder="Print da tela de bens e direitos"
              className={CAMPO}
            />
            <span className="mt-1 block text-xs leading-relaxed text-cinza">
              Uma frase curta. É o que aparece se a imagem não carregar, e o que um leitor de tela
              lê para quem não vê.
            </span>
          </label>
          {imagemEscolhida && (
            <img
              src={urlDaImagem(imagemPath)}
              alt=""
              className="max-h-56 rounded-xl border border-borda bg-white"
            />
          )}
        </div>
      )}

      {tipo === "video" && (
        <label className="mt-4 block">
          <span className="mb-1 block text-sm font-semibold text-titulo">
            Endereço do vídeo no YouTube
          </span>
          <input
            value={videoId}
            onChange={(e) => {
              // Cola da URL inteira é o caso comum, e é o que a equipe tem à mão.
              // Extrair o id aqui evita o iframe apontar para `watch?v=…` dentro de
              // `/embed/`, que dá "vídeo indisponível" sem dizer por quê.
              const bruto = e.target.value.trim();
              const m = bruto.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{6,})/);
              setVideoId(m ? m[1] : bruto);
            }}
            placeholder="Cole aqui o link do YouTube"
            className={CAMPO}
          />
          <span className="mt-1 block text-xs leading-relaxed text-cinza">
            Cole o link completo (`https://www.youtube.com/watch?v=…`) — o código do vídeo é
            reconhecido sozinho.
            {videoEscolhido && videoId.length !== 11 && (
              <strong className="mt-1 block text-alerta-tinta">
                Este código tem {videoId.length} caracteres, e o do YouTube tem 11. Confira, senão
                o vídeo não vai tocar.
              </strong>
            )}
          </span>
        </label>
      )}

      {/* A pré-visualização usa o MESMO renderizador do aluno. Duas versões do
          mesmo texto seria a segunda definição — e a que ninguém olha é a que fica
          errada. */}
      {(usaTexto || tipo === "lista" || (usaLegenda && texto.trim() !== "")) && (
        <div className="mt-5 rounded-xl bg-superficie-2 p-4">
          <p className="mb-2 text-xs font-bold tracking-wide text-cinza uppercase">
            Como o aluno vai ver
          </p>
          {tipo === "lista" ? (
            <ul
              className={`space-y-1 pl-5 text-sm leading-relaxed text-tinta ${
                ordenada ? "list-decimal" : "list-disc"
              }`}
            >
              {itens
                .split("\n")
                .map((l) => l.trim())
                .filter(Boolean)
                .map((l, i) => (
                  <li key={i}>
                    <TextoRico texto={l} />
                  </li>
                ))}
            </ul>
          ) : tipo === "titulo" ? (
            <p className="font-display text-lg font-bold text-titulo">
              <TextoRico texto={texto} />
            </p>
          ) : tipo === "destaque" ? (
            <div
              className={`rounded-xl border-l-4 p-3 text-sm leading-relaxed ${
                TONS_CLASSES[tom] ?? TONS_CLASSES.neutro
              }`}
            >
              <TextoRico texto={texto} />
            </div>
          ) : (
            <p className="text-sm leading-relaxed text-tinta">
              <TextoRico texto={texto} />
            </p>
          )}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <button type="submit" disabled={salvando} className="pill-cta disabled:opacity-60">
          {salvando ? "Salvando…" : "Salvar este bloco"}
        </button>
        <button type="button" onClick={aoCancelar} className="btn-contorno">
          Cancelar
        </button>
      </div>
      {erro && <Recado tipo="erro" texto={erro} />}
    </form>
  );
}

/** As mesmas classes de `FerramentaDeConteudo`, para a prévia não divergir do aluno. */
const TONS_CLASSES: Record<string, string> = {
  info: "bg-tile text-tinta border-azul-medio",
  atencao: "bg-alerta-fundo text-alerta-tinta border-laranja",
  positivo: "bg-aviso-fundo text-aviso-tinta border-sucesso",
  neutro: "bg-superficie-2 text-tinta border-borda",
};

// ---------------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------------
/** Troca duas posições de uma lista, devolvendo a ordem NOVA e completa. */
function trocar(ids: string[], de: number, para: number): string[] {
  const copia = [...ids];
  const [item] = copia.splice(de, 1);
  copia.splice(para, 0, item);
  return copia;
}

/**
 * Botão de AÇÃO da linha: ícone redondo, com o verbo inteiro no `title` e no
 * `aria-label`. Era pílula com o verbo por extenso — Victor pediu ícones nas
 * telas de admin (20/08): quatro pílulas por linha pesavam mais do que
 * explicavam. A palavra não sumiu, mudou de lugar: pairar o mouse ou usar
 * leitor de tela recebe a frase completa. O que continua em TEXTO é o que
 * ícone não carrega — contagens e as confirmações de excluir.
 */
function Botao({ onClick, title, icone }: { onClick: () => void; title: string; icone: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="grid size-9 shrink-0 place-items-center rounded-full border-[1.5px] border-borda text-tinta transition hover:border-laranja hover:text-laranja-texto"
    >
      {icone}
    </button>
  );
}

/**
 * Subir e descer, e NÃO arrastar.
 *
 * Arrasto não funciona em toque nem em teclado — o kanban do IR Proposta tem
 * arrasto **e** um `<select>` por card pela mesma razão. Aqui o `<select>` não cabe
 * (a ordem é relativa, não absoluta), então é o par de setas, com `aria-label` que
 * diz o que sobe: leitor de tela lendo "botão ▲" quatorze vezes não informa nada.
 */
function Setas({
  i,
  total,
  oQue,
  aoMover,
}: {
  i: number;
  total: number;
  oQue: string;
  aoMover: (de: number, para: number) => void;
}) {
  return (
    <div className="flex shrink-0 flex-col">
      <button
        type="button"
        disabled={i === 0}
        onClick={() => aoMover(i, i - 1)}
        aria-label={`Subir esta ${oQue}`}
        title={`Subir esta ${oQue}`}
        className="grid size-7 place-items-center rounded text-cinza transition hover:bg-chip hover:text-tinta disabled:opacity-25"
      >
        ▲
      </button>
      <button
        type="button"
        disabled={i === total - 1}
        onClick={() => aoMover(i, i + 1)}
        aria-label={`Descer esta ${oQue}`}
        title={`Descer esta ${oQue}`}
        className="grid size-7 place-items-center rounded text-cinza transition hover:bg-chip hover:text-tinta disabled:opacity-25"
      >
        ▼
      </button>
    </div>
  );
}

/**
 * O olho aberto/riscado — "o aluno vê isto ou não?".
 *
 * Era o texto "Visível"/"Escondida" (o par publicado/rascunho é vocabulário de
 * quem faz software); com o pedido dos ícones (Victor, 20/08) o ESTADO virou o
 * olho, com a cor de fundo sustentando a leitura de longe, e a frase inteira —
 * estado + o que o clique faz — foi para o `title` e o `aria-label`.
 */
function Visibilidade({ publicada, aoTrocar }: { publicada: boolean; aoTrocar: () => void }) {
  const rotulo = publicada
    ? "Visível para os alunos — clique para esconder"
    : "Escondida dos alunos — clique para publicar";
  return (
    <button
      type="button"
      onClick={aoTrocar}
      title={rotulo}
      aria-label={rotulo}
      className={`grid size-9 shrink-0 place-items-center rounded-full transition ${
        publicada
          ? "bg-aviso-fundo text-aviso-tinta hover:brightness-95"
          : "bg-chip text-chip-tinta hover:brightness-95"
      }`}
    >
      {publicada ? <IconeOlho /> : <IconeOlhoRiscado />}
    </button>
  );
}

/**
 * Excluir em DOIS toques, com o que se perde escrito por extenso.
 *
 * Não é cerimônia: área excluída leva as páginas e os blocos por cascade, e não há
 * lixeira. O texto do segundo toque diz o número de filhos porque "excluir a área
 * Renda Variável" e "excluir 11 páginas e 300 blocos" são a mesma ação com
 * consequências muito diferentes.
 */
function Excluir({
  oQue,
  nome,
  extra,
  aoConfirmar,
}: {
  oQue: string;
  nome: string;
  extra?: string;
  aoConfirmar: () => void;
}) {
  const [confirmando, setConfirmando] = useState(false);

  if (!confirmando) {
    // O primeiro toque virou lixeira (o pedido dos ícones, 20/08); a
    // CONFIRMAÇÃO continua em texto por extenso — o que se perde tem de estar
    // escrito antes do clique que perde.
    const rotulo = `Excluir ${oQue} “${nome}”`;
    return (
      <button
        type="button"
        onClick={() => setConfirmando(true)}
        title={rotulo}
        aria-label={rotulo}
        className="grid size-9 shrink-0 place-items-center rounded-full text-erro-tinta transition hover:bg-erro-fundo"
      >
        <IconeLixeira />
      </button>
    );
  }
  return (
    <span className="flex shrink-0 flex-wrap items-center gap-2 rounded-xl bg-erro-fundo px-3 py-2">
      <span className="text-sm leading-snug text-erro-tinta">
        Excluir {oQue} “{nome}”{extra ? ` e ${extra}` : ""}? Não tem como desfazer.
      </span>
      <button
        type="button"
        onClick={() => {
          setConfirmando(false);
          aoConfirmar();
        }}
        className="rounded-full bg-erro-tinta px-3 py-1 text-sm font-bold text-superficie"
      >
        Sim, excluir
      </button>
      <button
        type="button"
        onClick={() => setConfirmando(false)}
        className="text-sm font-semibold text-erro-tinta underline"
      >
        Não
      </button>
    </span>
  );
}

/**
 * Criar: pede só o NOME, e o endereço sai dele.
 *
 * A primeira versão pedia os dois campos lado a lado, e "endereço" é a palavra que
 * faz alguém leigo parar. Aqui ele é derivado e mostrado como consequência — dá
 * para ajustar, mas ninguém precisa decidir nada para criar uma página.
 */
function NovoItem({
  oQue,
  exemplo,
  aoCriar,
}: {
  oQue: string;
  exemplo: string;
  aoCriar: (nome: string, slug: string) => void;
}) {
  const [abrindo, setAbrindo] = useState(false);
  const [nome, setNome] = useState("");
  const [slug, setSlug] = useState("");
  const [tocouNoSlug, setTocouNoSlug] = useState(false);

  const sugerido = useMemo(() => slugificar(nome), [nome]);
  const slugFinal = tocouNoSlug ? slug : sugerido;

  if (!abrindo) {
    return (
      <button
        type="button"
        onClick={() => setAbrindo(true)}
        className="surface w-full p-4 text-left text-sm font-semibold text-link transition hover:border-laranja"
      >
        + Criar {oQue}
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (nome.trim() === "" || slugFinal === "") return;
        aoCriar(nome.trim(), slugFinal);
        setNome("");
        setSlug("");
        setTocouNoSlug(false);
        setAbrindo(false);
      }}
      className="surface p-4"
    >
      <p className="text-sm font-semibold text-titulo">Criar {oQue}</p>
      <label className="mt-3 block max-w-lg">
        <span className="mb-1 block text-sm font-semibold text-titulo">
          Nome que o aluno lê
        </span>
        <input
          autoFocus
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder={`Ex.: ${exemplo}`}
          className={CAMPO}
        />
      </label>
      <p className="mt-2 text-sm text-cinza">
        Endereço: <code className="text-xs">/{slugFinal || "…"}</code>{" "}
        <button
          type="button"
          onClick={() => {
            setTocouNoSlug(true);
            setSlug(sugerido);
          }}
          className="font-semibold text-link underline decoration-link/30 underline-offset-4"
        >
          ajustar
        </button>
      </p>
      {tocouNoSlug && (
        <label className="mt-2 block max-w-lg">
          <span className="mb-1 block text-sm font-semibold text-titulo">Endereço</span>
          <input
            value={slug}
            onChange={(e) => setSlug(slugificar(e.target.value))}
            className={CAMPO}
          />
        </label>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="submit" disabled={nome.trim() === ""} className="pill-cta disabled:opacity-60">
          Criar
        </button>
        <button type="button" onClick={() => setAbrindo(false)} className="btn-contorno">
          Cancelar
        </button>
      </div>
    </form>
  );
}

function BotaoDeMarca({ aoClicar, children }: { aoClicar: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={aoClicar}
      className="rounded-lg border border-borda px-2.5 py-1 text-sm text-tinta transition hover:border-laranja hover:bg-chip/50"
    >
      {children}
    </button>
  );
}

/** Mesma regra do banco (`^[a-z0-9]+(-[a-z0-9]+)*$`), aplicada enquanto se digita. */
function slugificar(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/**
 * O começo do conteúdo do bloco, para dar para reconhecê-lo sem abrir.
 *
 * `paraTextoSimples` tira a marcação: o resumo mostrava
 * `A tributação sobre os **__rendimentos__**…`, e para quem é leigo aquilo parece
 * defeito da tela, não marcação. Aqui a marcação não informa nada — o resumo
 * existe para reconhecer o bloco, e quem quer ver o negrito abre o Editar, onde a
 * prévia mostra como o aluno vê.
 */
function resumoDoBloco(b: BlocoEditavel): ReactNode {
  if (b.tipo === "lista") {
    return (
      (b.itens ?? []).map(paraTextoSimples).join(" · ") || (
        <span className="italic">lista vazia</span>
      )
    );
  }
  if (b.tipo === "imagem") {
    return b.imagem_path && !b.imagem_path.endsWith("a-escolher") ? (
      <span className="flex items-center gap-2">
        <img
          src={urlDaImagem(b.imagem_path)}
          alt=""
          className="size-10 rounded border border-borda bg-white object-cover"
        />
        {b.imagem_alt || "sem descrição"}
      </span>
    ) : (
      <span className="font-semibold text-alerta-tinta">Falta escolher a imagem</span>
    );
  }
  if (b.tipo === "video") {
    return b.video_id && b.video_id !== "a-escolher" ? (
      `vídeo ${b.video_id}`
    ) : (
      <span className="font-semibold text-alerta-tinta">Falta colar o link do vídeo</span>
    );
  }
  return paraTextoSimples(b.texto ?? "");
}
